import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '@/core/services/open-api/user.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, finalize, catchError, Observable, EMPTY } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CurrentUser } from '@/core/models/user';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@/core/services/toast.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-api-key',
    templateUrl: './api-key.component.html',
    styleUrls: ['./api-key.component.scss'],
    standalone: false,
})
export class ApiKeyComponent implements OnInit, OnDestroy {
    apiKey?: string | null;
    hasApiKey = false;
    isLoading = true;
    isPro = false;
    private readonly destroy$ = new Subject<void>();

    constructor(
        public readonly userService: UserService,
        private clipboard: Clipboard,
        private toastService: ToastService,
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    load(): void {
        if (this.apiKey !== undefined) {
            return;
        }
        this.isLoading = true;
        this.userService
            .loadCurrentUser()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false)),
                catchError((error) => this.handleLoadError(error)),
            )
            .subscribe({
                next: (currentUser: CurrentUser) => {
                    this.isPro = currentUser.isPro();
                    // sets API key if available.
                    if (currentUser.settings?.apiKey) {
                        this.setApiKey(currentUser.settings.apiKey);
                    }
                },
                error: (error: HttpErrorResponse) => {
                    console.error('Failed to load API key data:', error);
                    this.toastService.error('standard_error', 'standard_error');
                },
            });
    }

    private setApiKey(apiKey: string | null): void {
        this.apiKey = apiKey;
        this.hasApiKey = !!apiKey;
    }

    private handleLoadError(error: HttpErrorResponse): Observable<never> {
        console.error('API Key load error:', error);
        this.toastService.error('standard_error', 'standard_error');
        return EMPTY;
    }

    newAPIKey(): void {
        if (!this.isPro) {
            return;
        }
        this.isLoading = true;

        this.userService
            .createAPIKey()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.setApiKey(result);
                this.toastService.success('standard_success', 'api_key_created');
                this.isLoading = false;
            });
    }

    copyAPIKey(): void {
        if (this.apiKey != undefined) {
            try {
                const copied = this.clipboard.copy(this.apiKey ?? 'Error while copying api key.');
                if (copied) {
                    this.toastService.success('standard_success', 'api_key_copy_success');
                } else {
                    this.toastService.error('standard_error', 'api_key_copy_error');
                }
            } catch {
                this.toastService.error('standard_error', 'api_key_copy_error');
            }
        }
    }

    deleteAPIKey(): void {
        this.isLoading = true;

        this.userService
            .deleteAPIKey()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setApiKey(null);
                this.load();
                this.isLoading = false;
                this.toastService.success('standard_success', 'api_key_deleted');
            });
    }

    openDoc(): void {
        const baseUrl = environment.apiBaseUrl;
        const docUrl = `${baseUrl}/doc`;
        window.open(docUrl, '_blank', 'noopener');
    }
}
